import Box, { type BoxProps } from '@mui/material/Box'
import Table, { type TableProps } from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import {
  flexRender,
  type Cell,
  type Header,
  type HeaderGroup,
  type Row,
  type Table as TableType,
} from '@tanstack/react-table'
import { type VirtualItem, type Virtualizer } from '@tanstack/react-virtual'
import classNames from 'classnames/bind'
import { Fragment, type CSSProperties, type PropsWithChildren, type ReactElement, type RefObject } from 'react'
import styles from './DataTableBaseWp.module.scss'
import { TableRowWithCenterElementWp } from './TableRowWithCenterElementWp'

const cx = classNames.bind(styles)

const Placeholder = (props: PropsWithChildren<BoxProps>) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...props}
  />
)
export interface DataTableBaseProps<TData = unknown> {
  table: TableType<TData>
  rowVirtualizer?: Virtualizer<HTMLDivElement, Element>
  containerRef?: RefObject<HTMLDivElement>

  className?: TableProps['className']
  style?: TableProps['style']
  isEmpty?: boolean
  isLoading?: boolean
  maxHeight?: number | string
  maxWidth?: number | string
  width?: number | string
  renderSubComponent?: (props: { row: Row<TData> }) => ReactElement
  renderNoDataComponent?: () => ReactElement
  renderLoadingComponent?: () => ReactElement
}

export const DataTableBase = <TData, >(props: DataTableBaseProps<TData>) => {
  const {
    table,
    containerRef,
    className,
    style,
    isEmpty,
    isLoading,
    maxHeight,
    maxWidth,
    width,
    rowVirtualizer,
    renderSubComponent,
    renderNoDataComponent = () => <Placeholder>無資料</Placeholder>,
    renderLoadingComponent = () => <Placeholder>載入中...</Placeholder>,
  } = props

  const renderHeader = (header: Header<TData, unknown>) => {
    const { cellProps, isSortable } = header.column.columnDef.meta || {}
    const { colSpan, rowSpan, style, ...restCellProps } = cellProps || {}

    const label = flexRender(header.column.columnDef.header, header.getContext())

    const renderHeaderLabel = () => {
      if (header.isPlaceholder) return null
      if (isSortable) {
        return (
          <TableSortLabel
            onClick={header.column.getToggleSortingHandler()}
            direction={header.column.getIsSorted() || undefined}
          >
            {label}
          </TableSortLabel>
        )
      }
      return label
    }

    return (
      <TableCell
        key={header.id}
        className={cx('table-cell')}
        colSpan={colSpan ?? header.colSpan}
        rowSpan={rowSpan ?? header.rowSpan}
        style={{ width: `${header.getSize()}px`, ...style }}
        {...restCellProps}
      >
        {renderHeaderLabel()}
      </TableCell>
    )
  }

  const renderHeaderGroup = (headerGroup: HeaderGroup<TData>) => (
    <TableRow key={headerGroup.id} className={cx('table-row')}>
      {headerGroup.headers.map(renderHeader)}
    </TableRow>
  )

  const renderCell = (cell: Cell<TData, unknown>) => {
    const { cellProps } = cell.column.columnDef.meta || {}
    return (
      <TableCell
        key={cell.id}
        className={cx('table-cell')}
        style={{ width: cell.column.getSize() }}
        {...cellProps}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    )
  }

  const renderRow = (row: Row<TData>, virtualRow?: VirtualItem) => {
    const virtualProps
      = virtualRow && rowVirtualizer
        ? {
          'data-index': row.index,
          'ref': (node: HTMLTableRowElement) => rowVirtualizer.measureElement(node),

          'style': {
            transform: `translateY(${virtualRow.start}px)`,
            position: 'absolute',
          } as CSSProperties,
        }
        : {}
    return (
      <Fragment key={row.id}>
        <TableRow className={cx('table-row')} {...virtualProps}>
          {row.getVisibleCells().map(renderCell)}
        </TableRow>
        {row.getIsExpanded() && renderSubComponent?.({ row })}
      </Fragment>
    )
  }

  const renderRows = () => {
    const { rows } = table.getRowModel()
    const { pageSize } = table.getState().pagination

    if (isLoading) {
      // When the table per page is, say, 1000 rows, the row count has to be small enough to allow the user to see the loading icon.
      const loadingSize = 3
      return <TableRowWithCenterElementWp className={cx('table-row')} rowSize={loadingSize}>{renderLoadingComponent()}</TableRowWithCenterElementWp>
    }
    if (isEmpty) {
      return <TableRowWithCenterElementWp className={cx('table-row')} rowSize={pageSize}>{renderNoDataComponent()}</TableRowWithCenterElementWp>
    }
    if (rowVirtualizer) {
      return rowVirtualizer
        .getVirtualItems()
        // @ts-expect-error - TODO: fix this
        .map(virtualRow => renderRow(rows[virtualRow.index], virtualRow))
    }
    return rows.map(row => renderRow(row))
  }

  return (
    <>
      <div className={cx('root')}>
        <div
          className={cx('table', rowVirtualizer && 'is-virtualized', className)}
          style={{ maxHeight, maxWidth, width, ...style }}
          ref={containerRef}
        >
          <Table>
            <TableHead className={cx('table-head')}>
              {table.getHeaderGroups().map(renderHeaderGroup)}
            </TableHead>
            <TableBody
              className={cx('table-body')}
              sx={{ position: 'relative' }}
              style={{
                height: rowVirtualizer ? `${rowVirtualizer.getTotalSize()}px` : undefined,
              }}
            >
              {renderRows()}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}
